import {
  Row,
  Col,
  Card,
  Table,
  Button,
  Tag,
  DatePicker,
  Tooltip,
  Input,
  Select,
} from "antd";
import React, { useState, useEffect, useContext } from "react";
import useRequest from "../../hooks/useRequest";
import { ShowToast, Severty } from "../../helper/toast";
import ShowTotal from "../../components/ShowTotal";
import moment from "moment";
import apiPath from "../../constants/apiPath";
import lang from "../../helper/lang";
import useDebounce from "../../hooks/useDebounce";
import { AuthContext } from "../../context/AuthContext";
import { USERTYPE } from "../../constants/conts";
import { ExclamationCircleOutlined, UndoOutlined } from "@ant-design/icons";
import FilterBar, { FilterBarFilters } from "../../components/FilterBar";
import confirm from "antd/lib/modal/confirm";
import AddForm from "./AddForm";

const { RangePicker } = DatePicker;
const Search = Input.Search;
function Index() {
  const sectionName = lang("Level");
  const routeName = "level";

  const { userProfile, userPermission } = useContext(AuthContext);
  const { request } = useRequest();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [visible, setVisible] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [selected, setSelected] = useState();
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();
  const debouncedSearchText = useDebounce(searchText, 300);
  const [filters, setFilters] = useState({ status: undefined });
  const [showFilters, setShowFilters] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);
  const { Option } = Select;
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const isSuperAdmin = userProfile?.role === USERTYPE.SUPER_ADMIN;
  const isAddPermissionGranted =
    isSuperAdmin || userPermission?.BranchSection?.includes("add");

  // ----------- Fetch Data ---------------
  const fetchData = (pagination, filters) => {
    const params = new URLSearchParams();
    if (startDate) {
      params.append("startDate", startDate);
    }
    if (endDate) {
      params.append("endDate", endDate);
    }
    if (debouncedSearchText) {
      params.append("search", debouncedSearchText);
    }

    // 🔃 Status
    if (filters.status !== undefined) {
      params.append("status", filters.status);
    }
    request({
      url: `${apiPath.listLevels}?${params.toString()}`,
      method: "GET",
      onSuccess: ({ data }) => {
        setLoading(false);
        setList(() => data?.data);
        setPagination((prev) => ({
          current: pagination?.page,
          total: data?.total,
          pageSize: Number(data?.limit),
        }));
      },
      onError: (error) => {
        console.log(error);
        setLoading(false);
        ShowToast(error, Severty.ERROR);
      },
    });
  };
  const handleChange = (pagination, filters) => {
    fetchData(pagination, filters);
  };
  const onSearch = (e) => {
    setSearchText(e.target.value);
    setPagination({ current: 1, pageSize: pagination.pageSize });
  };
  const resetData = () => {
    setSearchText("");
    setStartDate("");
    setEndDate("");
    setFilters({
      status: undefined,
    });
    setRefresh((prev) => !prev);
  };

  const handleChangeDate = (e) => {
    if (e != null) {
      setStartDate(moment(e[0]._d).format("YYYY-MM-DD"));
      setEndDate(moment(e[1]._d).format("YYYY-MM-DD"));
    } else {
      setStartDate();
      setEndDate();
    }
  };

  const confirmStatus = (id, status) => {
    setTimeout(() => {
      confirm({
        maskClosable: true,
        icon: <ExclamationCircleOutlined />,
        className: "tab_modal",
        cancelButtonProps: { color: "red" },
        content: (
          <p
            style={{
              fontSize: "24px",
              fontWeight: "bold",
              textAlign: "center",
            }}
          >
            Are you sure you want change the status ?
          </p>
        ),
        onOk() {
          request({
            url: `${apiPath.changeLevelStatus}/${id}`,
            method: "PUT",
            data: {},
            onSuccess: (data) => {
              fetchData(pagination, filters);
              ShowToast(
                `${data.message || "Status Changed Successfully"}`,
                Severty.SUCCESS
              );
            },
            onError: (error) => {
              setLoading(false);
              ShowToast(error, Severty.ERROR);
            },
          });
        },
        onCancel() {
          console.log("Cancel");
        },
      });
    }, 5);
  };

  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => ({ ...prev, [filterName]: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const columns = [
    {
      title: `Level`,
      key: "name",
      dataIndex: "name",
      width: "25%",
      render: (name) => name,
    },
    {
      title: "Points",
      dataIndex: "points",
      width: "25%",
      render: (points = 0) => points,
    },
    {
      title: `${lang("Status")}`,
      key: "isActive",
      width: "25%",
      render: (_, { isActive, _id }) => {
        let color = isActive ? "green" : "red";
        return (
          <a>
            <Tag
              onClick={() => confirmStatus(_id, isActive)}
              color={color}
              key={isActive}
            >
              {isActive ? "Active" : "Inactive"}
            </Tag>
          </a>
        );
      },
    },
    {
      title: `Added On`,
      key: "createdAt",
      dataIndex: "createdAt",
      width: "25%",
      render: (createdAt) => moment(createdAt).format("DD-MM-YYYY"),
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    },
    {
      title: `${lang("Action")}`,
       
      render: (_, record) => {
        return (
          <>
            <Tooltip
              title={`Update Level`}
              color={"purple"}
              key={"updateLevel"}
            >
              <Button
                onClick={() => {
                  setSelected(record);
                  setVisible(true);
                  setIsUpdate(true);
                }}
              >
                <i className="fa fa-light fa-pen"></i>
              </Button>
            </Tooltip>
          </>
        );
      },
    },
  ];

  // ----------- Use Effects ----------
  useEffect(() => {
    setLoading(true);
    fetchData(pagination, filters);
  }, [filters, refresh, startDate, endDate, debouncedSearchText]);

  return (
    <>
      <div className="tabled">
        <Row gutter={[24, 0]}>
          <Col xs={24} xl={24}>
            <Card
              bordered={false}
              className="criclebox tablespace mb-24"
              title={sectionName + ` ${lang("Manager")}`}
              extra={
                <FilterBar
                  showFilters={showFilters}
                  onToggle={() => setShowFilters((v) => !v)}
                  searchContent={
                    <Search
                      size="large"
                      onChange={onSearch}
                      value={searchText}
                      onPressEnter={onSearch}
                      allowClear
                      placeholder="Search by name"
                    />
                  }
                  rightContent={
                    <Tooltip color={"red"}>
                      <Button onClick={(e) => setVisible(true)}>
                        Add Level
                      </Button>
                    </Tooltip>
                  }
                />
              }
            >
              {showFilters && (
                <FilterBarFilters>
                  <Select
                    placeholder="Status"
                    allowClear
                    value={filters.status}
                    onChange={(value) => handleFilterChange("status", value)}
                  >
                    <Option value={true}>Active</Option>
                    <Option value={false}>Inactive</Option>
                  </Select>
                  <RangePicker
                    disabledDate={(current) =>
                      current && current.isAfter(moment().endOf("day"))
                    }
                    onChange={handleChangeDate}
                    value={
                      startDate && endDate
                        ? [moment(startDate), moment(endDate)]
                        : null
                    }
                  />
                  <Button
                    onClick={() => resetData()}
                    type="primary"
                    icon={<UndoOutlined />}
                  >
                    Reset
                  </Button>
                </FilterBarFilters>
              )}
              <div
                style={{
                  display: "flex",
                  justifyContent: "end",
                  alignItems: "center",
                  marginBottom: "10px",
                  flexWrap: "wrap",
                }}
              >
                <h4 className="text-right m-0">
                  {" "}
                  {pagination.total
                    ? ShowTotal(pagination.total)
                    : ShowTotal(0)}{" "}
                </h4>
              </div>

              <div className="table-responsive customPagination customPaginationmain-newwwww">
                <Table
                  loading={loading}
                  columns={columns}
                  dataSource={list}
                  pagination={{
                    current: pagination.current,
                    defaultPageSize: 10,
                    responsive: true,
                    total: pagination.total,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    pageSizeOptions: ["10", "20", "30", "50"],
                  }}
                  onChange={handleChange}
                  className="ant-border-space"
                />
              </div>
            </Card>
          </Col>
        </Row>
      </div>

      {visible && (
        <AddForm
          type={sectionName}
          path={apiPath.addCategory}
          sectionName={sectionName}
          show={visible}
          hide={() => {
            setSelected();
            setVisible(false);
          }}
          data={selected}
          refresh={() => setRefresh((prev) => !prev)}
          isUpdate={isUpdate}
        />
      )}
    </>
  );
}

export default Index;
